import React, { useContext, useEffect, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, LinearProgress, Snackbar } from '@mui/material';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Tile from '../../../components/Tile/Tile';
import { ItemConfigurationPanel } from '../CommonComponents/ItemConfigurationPanel';
import { getDefaultPortalConfigurationByType } from '../../../services/pcwService';
import { configurableItemsTypes } from 'pages/Prequal/constants';
import _ from 'lodash';
import { portalConfigurationsActions } from '../../../redux/portalConfigurationReducer';
import { PortalConfigurationType } from '@trnsact/trnsact-shared-types';
import {
  PortalConfigContainer,
  PortalManagement,
  ExternalDashboardManagement,
  SmartSubmitManagement,
  AiAssistantManagement,
} from 'modules/portalConfigurations';
import { MagicEmailInboxCard } from '../../PortalSetup/MagicEmailInboxCard';
import { PaynetSettingsCard } from '../../PortalSetup/PaynetSettingsCard';
import { PartnerProfileManagement } from '../../../modules/portalConfigurations/partnerProfile/ui/PartnerProfileManagement';
import { CommonDataContext } from '../../../contexts/CommonDataContext';

const QUERY_PORTAL_CONFIGURATION = gql`
  query ($vendorProfileId: ID!, $types: [PortalConfigurationType]) {
    portalConfigurations(vendorProfileId: $vendorProfileId, types: $types) {
      portalConfigurationId
      vendorProfileId
      jsonDefinition
      type
    }
  }
`;

const SAVE_PORTAL_CONFIGURATION = gql`
  mutation ($input: UpsertPortalConfigurationInput!) {
    savePortalConfiguration(input: $input)
  }
`;

const AccessControlContainer = () => {
  const { isSuperAdmin } = useContext(CommonDataContext);
  const account = useSelector(state => state.account);
  const lenderProfiles = useSelector(state => state.lp);
  const [pcwConfigurations, setPCWConfigurations] = useState([]);
  const [configurableItemsSettings, setConfigurableItemsSettings] = useState([]);
  const [savePortalConfigurationSuccess, setSavePortalConfigurationSuccess] = useState(false);
  const [savePortalConfigurationError, setSavePortalConfigurationError] = useState(false);

  const accessControlPortalConfigurationTypes = [
    PortalConfigurationType.Scorecard,
    PortalConfigurationType.Leads,
    PortalConfigurationType.Opportunities,
    PortalConfigurationType.UserManagement,
    PortalConfigurationType.SecurityManagementMfa,
    PortalConfigurationType.Aftermarket,
    PortalConfigurationType.HealthCheck,
    PortalConfigurationType.CreditReports,
    PortalConfigurationType.Documents,
    PortalConfigurationType.PortalSetup,
    PortalConfigurationType.PrescreenManagement,
    PortalConfigurationType.SubmissionsHubManagement,
    PortalConfigurationType.InvoiceAndEquipment,
    PortalConfigurationType.Insurance,
    PortalConfigurationType.Inventory,
  ];

  const accessControlConfigurableItemsTypes = [
    configurableItemsTypes.scorecard,
    configurableItemsTypes.leads,
    configurableItemsTypes.opportunities,
    configurableItemsTypes.userManagement,
    configurableItemsTypes.securityManagement,
    configurableItemsTypes.aftermarket,
    configurableItemsTypes.healthCheck,
    configurableItemsTypes.creditReports,
    configurableItemsTypes.documents,
    configurableItemsTypes.portalSetup,
    configurableItemsTypes.prescreen,
    configurableItemsTypes.submissionsHubManagement,
    configurableItemsTypes.invoiceAndEquipment,
    configurableItemsTypes.insurance,
    configurableItemsTypes.inventory,
  ];

  const configurableItemsTypesToPortalConfigurationTypesMap = {
    [configurableItemsTypes.scorecard]: PortalConfigurationType.Scorecard,
    [configurableItemsTypes.leads]: PortalConfigurationType.Leads,
    [configurableItemsTypes.opportunities]: PortalConfigurationType.Opportunities,
    [configurableItemsTypes.userManagement]: PortalConfigurationType.UserManagement,
    [configurableItemsTypes.securityManagement]: PortalConfigurationType.SecurityManagementMfa,
    [configurableItemsTypes.aftermarket]: PortalConfigurationType.Aftermarket,
    [configurableItemsTypes.healthCheck]: PortalConfigurationType.HealthCheck,
    [configurableItemsTypes.creditReports]: PortalConfigurationType.CreditReports,
    [configurableItemsTypes.documents]: PortalConfigurationType.Documents,
    [configurableItemsTypes.portalSetup]: PortalConfigurationType.PortalSetup,
    [configurableItemsTypes.prescreen]: PortalConfigurationType.PrescreenManagement,
    [configurableItemsTypes.submissionsHubManagement]: PortalConfigurationType.SubmissionsHubManagement,
    [configurableItemsTypes.invoiceAndEquipment]: PortalConfigurationType.InvoiceAndEquipment,
    [configurableItemsTypes.insurance]: PortalConfigurationType.Insurance,
    [configurableItemsTypes.inventory]: PortalConfigurationType.Inventory,
  };

  const dispatch = useDispatch();

  const {
    data: portalConfigurationData,
    loading,
    error,
    refetch,
  } = useQuery(QUERY_PORTAL_CONFIGURATION, {
    variables: {
      vendorProfileId: account.vendorProfileId,
      types: accessControlPortalConfigurationTypes,
    },
    fetchPolicy: 'no-cache',
  });

  const [savePortalConfiguration] = useMutation(SAVE_PORTAL_CONFIGURATION, {
    context: { authRequired: true },
    onCompleted() {
      setSavePortalConfigurationSuccess(true);
    },
    onError() {
      setSavePortalConfigurationError(true);
    },
  });

  const getConfigurableItemSettings = (configurableItemType, portalConfiguration) => {
    const getDefaultConfigurableItemSettings = (configurableItemType, portalConfiguration) => {
      return {
        item: portalConfiguration.jsonDefinition,
        type: configurableItemType,
        extraData: {
          portalConfigurationType: portalConfiguration.type,
        },
      };
    };

    const getScorecardConfigurableItemSettings = defaultConfigurableItemSettings => {
      const scorecardSpecificSettings = {
        extraData: {
          dataByConfigurationKey: {
            lenderProfileId: {
              options: lenderProfiles,
              getOptionLabelFn: lenderProfile => _.get(lenderProfile, 'lenderName', ''),
              placeholder: 'Choose Lender',
              optionKeyToSave: 'lenderProfileId',
            },
          },
        },
      };
      return _.merge({}, defaultConfigurableItemSettings, scorecardSpecificSettings);
    };

    const getDocumentsConfigurableItemSettings = defaultConfigurableItemSettings => {
      const documentsSpecificSettings = {
        extraData: {
          dataByConfigurationKey: {
            creditAppHiddenForLenderProfileIds: {
              options: lenderProfiles,
              getOptionLabelFn: lenderProfile => _.get(lenderProfile, 'lenderName', ''),
              //placeholder: "Choose Lenders",
              multiple: true,
              isInversed: true,
              optionKeyToSave: 'lenderProfileId',
            },
          },
        },
        configurationKeysToDisplay: [
          'creditAppHiddenForLenderProfileIds',
          /* "creditReportsHiddenForLenderProfileIds", */
          'rolesSettings',
        ],
      };
      return _.merge({}, defaultConfigurableItemSettings, documentsSpecificSettings);
    };

    const getPrescreenConfigurableItemSettings = defaultConfigurableItemSettings => defaultConfigurableItemSettings;
    const getSubmissionsHubManagementConfigurableItemSettings = defaultConfigurableItemSettings =>
      defaultConfigurableItemSettings;

    const mergePortalSetupAPIConfigWithDefaultConfig = apiConfig => {
      const defaultConfig = getDefaultPortalConfigurationByType(PortalConfigurationType.PortalSetup);

      return {
        ...apiConfig,
        item: {
          ...apiConfig.item,
          config: {
            ...apiConfig.item.config,
            rolesSettings: {
              ...(defaultConfig?.jsonDefinition?.config?.rolesSettings ?? {}),
              ...apiConfig.item.config.rolesSettings,
            },
          },
        },
      };
    };

    const mergePrescreenAPIConfigWithDefaultConfig = apiConfig => {
      const defaultConfig = getDefaultPortalConfigurationByType(PortalConfigurationType.PrescreenManagement);
      return {
        ...apiConfig,
        item: {
          ...apiConfig.item,
          config: {
            ...(defaultConfig?.jsonDefinition?.config ?? {}),
            ...apiConfig.item.config,
          },
        },
      };
    };

    const getAftermarketConfigurableItemSettings = defaultConfigurableItemSettings => {
      const pcwKeys = ['aftermarketLicenceType', 'allowCreateCustomProducts', 'partnerAllowSendProposals'];
      const configurationKeysToDisplay = isSuperAdmin ? [...pcwKeys, 'rolesSettings'] : pcwKeys;

      return { ...defaultConfigurableItemSettings, configurationKeysToDisplay };
    };

    const getSettingsByConfigurableItemTypeHandlers = {
      [configurableItemsTypes.scorecard]: getScorecardConfigurableItemSettings,
      [configurableItemsTypes.documents]: getDocumentsConfigurableItemSettings,
      [configurableItemsTypes.prescreen]: mergePrescreenAPIConfigWithDefaultConfig,
      [configurableItemsTypes.submissionsHubManagement]: getSubmissionsHubManagementConfigurableItemSettings,
      [configurableItemsTypes.portalSetup]: mergePortalSetupAPIConfigWithDefaultConfig,
      [configurableItemsTypes.aftermarket]: getAftermarketConfigurableItemSettings,
    };

    const defaultConfigurableItemSettings = getDefaultConfigurableItemSettings(
      configurableItemType,
      portalConfiguration
    );

    return getSettingsByConfigurableItemTypeHandlers[configurableItemType]
      ? getSettingsByConfigurableItemTypeHandlers[configurableItemType](defaultConfigurableItemSettings)
      : defaultConfigurableItemSettings;
  };

  useEffect(() => {
    if (!portalConfigurationData) {
      return;
    }

    const pcwConfigurationsToSet = _.map(accessControlPortalConfigurationTypes, portalConfigurationType => {
      const savedConfiguration = _.find(portalConfigurationData.portalConfigurations, {
        type: portalConfigurationType,
      });
      const configurationToSet = _.some(savedConfiguration)
        ? savedConfiguration
        : getDefaultPortalConfigurationByType(portalConfigurationType, account.vendorProfileId);
      delete configurationToSet.__typename;
      return configurationToSet;
    });

    setPCWConfigurations(pcwConfigurationsToSet);
  }, [portalConfigurationData]);

  useEffect(() => {
    if (_.isEmpty(pcwConfigurations)) {
      return;
    }

    const configurableItemsSettingsToSet = _.map(accessControlConfigurableItemsTypes, configurableItemType => {
      const portalConfigurationType = configurableItemsTypesToPortalConfigurationTypesMap[configurableItemType];
      const portalConfiguration = _.find(pcwConfigurations, { type: portalConfigurationType });
      return getConfigurableItemSettings(configurableItemType, portalConfiguration);
    });

    setConfigurableItemsSettings(configurableItemsSettingsToSet);
  }, [pcwConfigurations]);

  const saveItemConfig = async configurableItemSettings => {
    const portalConfigurationType = _.get(configurableItemSettings, 'extraData.portalConfigurationType');

    const configurationToSave = _.find(pcwConfigurations, { type: portalConfigurationType });

    try {
      await savePortalConfiguration({
        variables: {
          input: {
            ...configurationToSave,
            jsonDefinition: configurableItemSettings.item,
          },
        },
      }).then(({ data }) => {
        if (configurationToSave.portalConfigurationId) return;

        setPCWConfigurations(prevPCWConfigurations => {
          return prevPCWConfigurations.map(prevPCWConfiguration => {
            if (prevPCWConfiguration.type === portalConfigurationType) {
              return {
                ...prevPCWConfiguration,
                portalConfigurationId: _.get(data, 'savePortalConfiguration', null),
              };
            }
            return prevPCWConfiguration;
          });
        });
      });

      const { data } = await refetch();
      dispatch(portalConfigurationsActions.setConfigData(data?.portalConfigurations));
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      {loading && <LinearProgress />}

      {error && <h3>{String(error)}</h3>}

      {!loading && (
        <Grid container spacing={4}>
          {configurableItemsSettings.map(configurableItemSettings => (
            <Grid item sm={12} md={6} key={configurableItemSettings.type}>
              <Tile>
                <ItemConfigurationPanel
                  configurableItemSettings={configurableItemSettings}
                  onSaveFn={saveItemConfig}
                  account={account}
                />
              </Tile>
            </Grid>
          ))}

          <PortalConfigContainer
            portalConfigTypes={[
              PortalConfigurationType.Portal,
              PortalConfigurationType.SmartSubmit,
              PortalConfigurationType.AiAssistant,
            ]}
          >
            {renderProps => (
              <>
                <PortalManagement {...renderProps} />
                <ExternalDashboardManagement {...renderProps} />
                <SmartSubmitManagement type={PortalConfigurationType.SmartSubmit} {...renderProps} />
                <AiAssistantManagement type={PortalConfigurationType.AiAssistant} {...renderProps} />
                <PartnerProfileManagement {...renderProps} />
              </>
            )}
          </PortalConfigContainer>

          <Grid item xs={12}>
            <MagicEmailInboxCard />
          </Grid>

          <Grid item xs={12} md={6}>
            <PaynetSettingsCard />
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={savePortalConfigurationSuccess}
        autoHideDuration={3000}
        onClose={() => setSavePortalConfigurationSuccess(state => !state)}
      >
        <Alert severity="success">
          <AlertTitle>Changes Saved!</AlertTitle>
        </Alert>
      </Snackbar>

      <Snackbar
        open={savePortalConfigurationError}
        autoHideDuration={3000}
        onClose={() => setSavePortalConfigurationError(state => !state)}
      >
        <Alert severity="error">
          <AlertTitle>Error on saving</AlertTitle>
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AccessControlContainer;
